import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from '../../../utils/translation';
import { useImageComponent, useLinkComponent } from '../../../utils/component';
import { Cta } from '../../cta';
import type { LinkProps } from '../../../atoms/types';
import { StarsMark } from '../../stars-with-mark';
import { DEVICE_SIZE, mq } from '../../../utils/mq';

export type SoftwareCardProps = {
  title?: string;
  rating?: number;
  countRating?: number;
  countDownloads?: number;
  image: { imageId: string; alt: string; extension?: string; title?: string };
  link?: LinkProps;
};

const Card = styled.div`
  align-self: stretch;
  background: var(--theme-color-background);
  border-radius: 6px;
  box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.05);
  margin: 0 0 20px;
  max-width: 80vw;
  display: flex;
  flex-direction: column;

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      min-width: 80vw;
    `,
  )}
`;

const CardImage = styled.div`
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  display: flex;
  overflow: hidden;
  padding: var(--spacer-fluid) var(--spacer-fluid) 0;
  justify-content: center;
`;

const CardBody = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  gap: var(--spacer-s);
  padding: var(--spacer-s) var(--spacer-fluid) var(--spacer-fluid);
  flex: 1;
`;

const CardTitle = styled.div`
  color: var(--title-foreground);
  font-family: var(--theme-typography-primary-font);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.38;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.3s;

  &:hover {
    color: var(--theme-color-primary);
  }
`;

const CardStarsMark = styled.div`
  align-self: center;
  margin-bottom: auto;
`;

const CountDownloadsLabel = styled.div`
  text-align: center;
  padding: var(--spacer-xs) 0 0;
`;

const CountRatingLabel = styled.div`
  font-weight: bold;
  text-align: center;
`;

export function SimpleSoftwareCard({ title, rating, countRating, countDownloads, image, link }: SoftwareCardProps) {
  const t = useTranslation();
  const ImageComponent = useImageComponent();
  const LinkComponent = useLinkComponent();

  const imageProps = {
    ...image,
    mobileWidth: 212,
    mobileHeight: 212,
    desktopWidth: 212,
    desktopHeight: 212,
    disableRadius: true,
  };

  return (
    <Card>
      <CardImage>
        <LinkComponent {...link}>
          <ImageComponent {...imageProps} />
        </LinkComponent>
      </CardImage>
      <CardBody>
        {link?.href && (
          <CardTitle title={title}>
            <LinkComponent {...link}>{title}</LinkComponent>
          </CardTitle>
        )}
        {link?.href && (
          <Cta className="mod-green mod-sm" {...link}>
            {t('design.download.download')}
          </Cta>
        )}
        {rating ? (
          <CardStarsMark>
            <StarsMark rating={rating / 2} max={5} ratingLabel={rating} maxLabel={10} />
          </CardStarsMark>
        ) : null}
        {countDownloads && <CountDownloadsLabel>{countDownloads} téléchargements</CountDownloadsLabel>}
        {countRating && <CountRatingLabel>{countRating} notes</CountRatingLabel>}
      </CardBody>
    </Card>
  );
}
